import React, { useRef, useState, useEffect } from "react";
import { Route, Link, useHistory, useLocation, Switch } from "react-router-dom";
import {
  FaPencilAlt,
  FaShieldAlt,
  FaIndustry,
  FaUsers,
  FaExclamationTriangle,
  FaChevronDown,
  FaCog,
  FaPercent,
} from "react-icons/fa";

import MomoPayment from "./components/MomoPayment";
import Profile from "./components/Profile";
import CompanyProfile from "./components/CompanyProfile";
import Password from "./components/Password";
import Groups from "./components/Groups";
import DangerZone from "./components/DangerZone";
import AmenitiesManagement from "./components/AmenitiesManagement";
import RevenueShareManagement from "./components/RevenueShareManagement";

import * as SERVICES from "../../services/localService";
import { apiV2Service } from "../../services/apiV2Service";
import logger from "../../utils/logger";
import { useAnalytics } from "../../components/Analytics";
import { AnalyticsEvents, AnalyticsProps } from "../../utils/analytics";
import LoadingSpinner from "../../components/LoadingSpinner";
import { FeatureAccess } from "../../utils/featureAccessControl";
import { useCountries } from "../../hooks/useCountries";
import { useCurrencies } from "../../hooks/useCurrencies";

const Settings = () => {
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const imageInput = useRef(null);
  const [currentRoute, setCurrentRoute] = useState();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();

  // Load countries and currencies
  const { countries, countriesLoading } = useCountries();
  const { currencies, currenciesLoading } = useCurrencies();

  // Helper to persist user and notify layout/header of changes (e.g., avatar updates)
  const updateUserEverywhere = user => {
    if (!user) return;
    setCurrentUser(user);
    SERVICES.saveUser(user);
    window.dispatchEvent(new CustomEvent("currentUserUpdated", { detail: user }));
  };

  // Add analytics tracking
  const { trackEvent } = useAnalytics();

  // Fetch current user data from API
  const fetchCurrentUser = async () => {
    try {
      setIsLoadingUser(true);
      const response = await apiV2Service.getSelfUser();
      logger.log("Settings - Current user data:", response);

      // Extract user data from API response
      const userData = response.data;
      updateUserEverywhere(userData);

      // Set user role - map from organizationRole.code or fallback to stored role
      const userRole = userData?.organizationRole?.code || SERVICES.getUser()?.role || "";
      setCurrentUserRole(userRole);

      // Track settings page initial load
      const userName = userData ? `${userData.firstName || ""} ${userData.lastName || ""}`.trim() : "";
      const userId = userData?.id;
      const companyId = userData?.organizationId;
      const email = userData?.emailAddress;

      trackEvent(AnalyticsEvents.SETTINGS_SECTION_VIEWED, {
        [AnalyticsProps.PAGE_NAME]: "settings_home",
        [AnalyticsProps.USER_ROLE]: userRole,
        [AnalyticsProps.USER_ID]: userId,
        [AnalyticsProps.COMPANY_ID]: companyId,
        [AnalyticsProps.USER_NAME]: userName,
        [AnalyticsProps.EMAIL]: email,
        [AnalyticsProps.SECTION]: "profile",
        [AnalyticsProps.IS_SUCCESS]: true,
        initial_load: true,
      });
    } catch (error) {
      logger.error("Settings - Failed to fetch current user:", error);
      // Fallback to localStorage data if API fails
      const fallbackUser = SERVICES.getUser();
      updateUserEverywhere(fallbackUser);
      setCurrentUserRole(fallbackUser?.role || "");
    } finally {
      setIsLoadingUser(false);
    }
  };

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    // Track page view when settings section changes
    if (!currentUser) return; // Wait for user data to be loaded

    const path = location.pathname;
    let section = path.split("/settings/")[1] || "profile";

    const userId = currentUser?.id;
    const companyId = currentUser?.organizationId;
    const email = currentUser?.emailAddress;

    // Transform section name to be consistent
    if (section.includes("company/profile")) section = "company_profile";
    if (section === "danger-zone") section = "danger_zone";

    trackEvent(AnalyticsEvents.SETTINGS_SECTION_VIEWED, {
      [AnalyticsProps.PAGE_NAME]: `settings_${section}`,
      [AnalyticsProps.SECTION]: section,
      [AnalyticsProps.USER_ROLE]: currentUserRole,
      [AnalyticsProps.USER_ID]: userId,
      [AnalyticsProps.COMPANY_ID]: companyId,
      [AnalyticsProps.EMAIL]: email,
      [AnalyticsProps.IS_SUCCESS]: true,
      navigation_method: "url_change",
    });
  }, [location.pathname, currentUserRole, currentUser]);

  useEffect(() => {
    // Update current route based on location pathname
    const path = location.pathname;
    if (path === "/settings") setCurrentRoute("/settings");
    else if (path === "/settings/security") setCurrentRoute("/settings/security");
    else if (path.includes("/settings/company/profile")) setCurrentRoute("/settings/company/profile");
    else if (path === "/settings/groups") setCurrentRoute("/settings/groups");
    else if (path === "/settings/amenities") setCurrentRoute("/settings/amenities");
    else if (path === "/settings/danger-zone") setCurrentRoute("/settings/danger-zone");
    else if (path === "/settings/momopayment") setCurrentRoute("/settings/momopayment");
  }, [location.pathname]);

  const triggerImage = () => {
    logger.log(imageInput.current);
    imageInput.current.click();

    const userId = currentUser?.id;

    // Track image upload initiation
    trackEvent(AnalyticsEvents.SETTINGS_ACTION, {
      [AnalyticsProps.ACTION_TYPE]: "trigger_image_upload",
      [AnalyticsProps.USER_ROLE]: currentUserRole,
      [AnalyticsProps.USER_ID]: userId,
      [AnalyticsProps.COMPONENT_NAME]: "Settings",
      [AnalyticsProps.IS_SUCCESS]: true,
    });
  };

  const handleSelect = eventKey => {
    setCurrentRoute(eventKey);

    const userId = currentUser?.id;
    const companyId = currentUser?.organizationId;
    const email = currentUser?.emailAddress;

    // Track settings navigation
    trackEvent(AnalyticsEvents.SETTINGS_SECTION_VIEWED, {
      [AnalyticsProps.SECTION]: eventKey,
      [AnalyticsProps.PAGE_NAME]: `settings_${eventKey}`,
      [AnalyticsProps.USER_ROLE]: currentUserRole,
      [AnalyticsProps.USER_ID]: userId,
      [AnalyticsProps.COMPANY_ID]: companyId,
      [AnalyticsProps.EMAIL]: email,
      [AnalyticsProps.IS_SUCCESS]: true,
      navigation_method: "nav_select",
    });
  };

  const handleNavClick = section => {
    const userId = currentUser?.id;
    const companyId = currentUser?.organizationId;
    const email = currentUser?.emailAddress;

    // Close dropdown if open
    setIsDropdownOpen(false);

    // Track navigation click with standard event
    trackEvent(AnalyticsEvents.SETTINGS_SECTION_CLICKED, {
      [AnalyticsProps.SECTION]: section,
      [AnalyticsProps.PAGE_NAME]: `settings_${section}`,
      [AnalyticsProps.USER_ROLE]: currentUserRole,
      [AnalyticsProps.USER_ID]: userId,
      [AnalyticsProps.COMPANY_ID]: companyId,
      [AnalyticsProps.EMAIL]: email,
      [AnalyticsProps.COMPONENT_NAME]: "SettingsNav",
      [AnalyticsProps.IS_SUCCESS]: true,
      interaction_type: "click",
    });
  };

  const orgTypeCode = currentUser?.organization?.type?.code;
  const isCorporateCustomer = orgTypeCode === "CORPORATE_CUSTOMER";
  const isRequiredRole = currentUserRole === "ROLE_APPROVER_EMPLOYEE" || currentUserRole === "ROLE_SUBSCRIBER_OWNER";

  const canManageGroups =
    ((currentUserRole === "ROLE_SUBSCRIBER_OWNER" || currentUserRole === "ROLE_SUBSCRIBER_INDIVIDUAL") &&
      FeatureAccess.hasTeamsAccess(currentUser)) ||
    (isCorporateCustomer && isRequiredRole);

  // Function to determine if a nav item is active
  const isActive = path => currentRoute === path;

  // Get current page title
  const getCurrentPageTitle = () => {
    if (currentRoute === "/settings") return "Edit Profile";
    if (currentRoute === "/settings/security") return "Security";
    if (currentRoute === "/settings/company/profile") return "Company Profile";
    if (currentRoute === "/settings/groups") return "Team Management";
    if (currentRoute === "/settings/amenities") return "Amenities Management";
    if (currentRoute === "/settings/revenue-share") return "Revenue Share Management";
    if (currentRoute === "/settings/danger-zone") return "Danger Zone";
    return "Settings";
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Show loading state while fetching user data
  if (isLoadingUser) {
    return (
      <div className="container px-4 py-4 mx-auto md:py-6">
        <div className="mb-4 md:mb-6">
          <h1 className="text-xl font-semibold text-gray-800 md:text-2xl">Settings</h1>
        </div>
        <LoadingSpinner loading={true} message="Loading user data..." padding="py-8" className="text-blue-600" />
      </div>
    );
  }

  return (
    <div className="container px-4 py-4 mx-auto md:py-6">
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl font-semibold text-gray-800 md:text-2xl">Settings</h1>
      </div>

      <div className="overflow-hidden bg-white rounded-lg shadow-sm">
        <div className="flex flex-col md:flex-row">
          {/* Mobile Navigation - Dropdown Select Design */}
          <div className="w-full bg-gray-50 border-b border-gray-200 md:hidden">
            <div className="relative px-4 py-3">
              <button
                onClick={toggleDropdown}
                className="flex justify-between items-center px-4 py-2 w-full text-sm bg-white rounded-md border border-gray-300 shadow-sm"
              >
                <div className="flex items-center">
                  {currentRoute === "/settings" && <FaPencilAlt className="mr-2 text-gray-500" />}
                  {currentRoute === "/settings/security" && <FaShieldAlt className="mr-2 text-gray-500" />}
                  {currentRoute === "/settings/company/profile" && <FaIndustry className="mr-2 text-gray-500" />}
                  {currentRoute === "/settings/groups" && <FaUsers className="mr-2 text-gray-500" />}
                  {currentRoute === "/settings/amenities" && <FaCog className="mr-2 text-gray-500" />}
                  {currentRoute === "/settings/revenue-share" && <FaPercent className="mr-2 text-gray-500" />}
                  {currentRoute === "/settings/danger-zone" && <FaExclamationTriangle className="mr-2 text-red-500" />}
                  <span>{getCurrentPageTitle()}</span>
                </div>
                <FaChevronDown
                  className={`transition-transform duration-200 ${isDropdownOpen ? "transform rotate-180" : ""}`}
                />
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 left-0 z-10 mx-4 mt-1 bg-white rounded-md border border-gray-200 shadow-lg">
                  <div className="py-1">
                    <Link
                      to="/settings"
                      className={`flex items-center px-4 py-2 text-sm ${isActive("/settings") ? "text-blue-600 bg-blue-50" : "text-gray-700 hover:bg-gray-50"}`}
                      onClick={() => handleNavClick("profile")}
                    >
                      <FaPencilAlt className="mr-3 text-lg" />
                      Edit Profile
                    </Link>

                    <Link
                      to="/settings/security"
                      className={`flex items-center px-4 py-2 text-sm ${isActive("/settings/security") ? "text-blue-600 bg-blue-50" : "text-gray-700 hover:bg-gray-50"}`}
                      onClick={() => handleNavClick("security")}
                    >
                      <FaShieldAlt className="mr-3 text-lg" />
                      Security
                    </Link>

                    {currentUserRole &&
                      (currentUserRole === "ROLE_SUBSCRIBER_OWNER" ||
                        currentUserRole === "ROLE_HOST" ||
                        currentUserRole === "ROLE_ADMINISTRATOR") && (
                        <Link
                          to="/settings/company/profile"
                          className={`flex items-center px-4 py-2 text-sm ${isActive("/settings/company/profile") ? "text-blue-600 bg-blue-50" : "text-gray-700 hover:bg-gray-50"}`}
                          onClick={() => handleNavClick("company_profile")}
                        >
                          <FaIndustry className="mr-3 text-lg" />
                          Company Profile
                        </Link>
                      )}

                    {canManageGroups && (
                      <Link
                        to="/settings/groups"
                        className={`flex items-center px-4 py-2 text-sm ${isActive("/settings/groups") ? "text-blue-600 bg-blue-50" : "text-gray-700 hover:bg-gray-50"}`}
                        onClick={() => handleNavClick("groups")}
                      >
                        <FaUsers className="mr-3 text-lg" />
                        Team Management
                      </Link>
                    )}

                    {currentUserRole === "ROLE_ADMINISTRATOR" && (
                      <Link
                        to="/settings/amenities"
                        className={`flex items-center px-4 py-2 text-sm ${isActive("/settings/amenities") ? "text-blue-600 bg-blue-50" : "text-gray-700 hover:bg-gray-50"}`}
                        onClick={() => handleNavClick("amenities")}
                      >
                        <FaCog className="mr-3 text-lg" />
                        Amenities Management
                      </Link>
                    )}

                    {currentUserRole === "ROLE_ADMINISTRATOR" && (
                      <Link
                        to="/settings/revenue-share"
                        className={`flex items-center px-4 py-2 text-sm ${isActive("/settings/revenue-share") ? "text-blue-600 bg-blue-50" : "text-gray-700 hover:bg-gray-50"}`}
                        onClick={() => handleNavClick("revenue_share")}
                      >
                        <FaPercent className="mr-3 text-lg" />
                        Revenue Share Management
                      </Link>
                    )}

                    {currentUserRole && currentUserRole !== "ROLE_ADMINISTRATOR" && (
                      <Link
                        to="/settings/danger-zone"
                        className={`flex items-center px-4 py-2 text-sm ${isActive("/settings/danger-zone") ? "text-red-600 bg-red-50" : "text-red-600 hover:bg-red-50"}`}
                        onClick={() => handleNavClick("danger_zone")}
                      >
                        <FaExclamationTriangle className="mr-3 text-lg" />
                        Danger Zone
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col flex-1 md:flex-row">
            {/* Desktop Sidebar */}
            <div className="hidden w-64 bg-gray-50 border-r border-gray-200 md:block">
              <div className="px-1 py-4">
                <nav className="space-y-1">
                  <Link
                    to="/settings"
                    className={`flex items-center px-4 py-3 text-sm font-medium rounded-md ${
                      isActive("/settings") ? "text-blue-600 bg-blue-50" : "text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={() => handleNavClick("profile")}
                  >
                    <FaPencilAlt className="mr-3 text-lg" />
                    Edit Profile
                  </Link>

                  <Link
                    to="/settings/security"
                    className={`flex items-center px-4 py-3 text-sm font-medium rounded-md ${
                      isActive("/settings/security") ? "text-blue-600 bg-blue-50" : "text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={() => handleNavClick("security")}
                  >
                    <FaShieldAlt className="mr-3 text-lg" />
                    Password & Security
                  </Link>

                  {currentUserRole &&
                    (currentUserRole === "ROLE_SUBSCRIBER_OWNER" ||
                      currentUserRole === "ROLE_HOST" ||
                      currentUserRole === "ROLE_ADMINISTRATOR") && (
                      <Link
                        to="/settings/company/profile"
                        className={`flex items-center px-4 py-3 text-sm font-medium rounded-md ${
                          isActive("/settings/company/profile")
                            ? "text-blue-600 bg-blue-50"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                        onClick={() => handleNavClick("company_profile")}
                      >
                        <FaIndustry className="mr-3 text-lg" />
                        Company Profile
                      </Link>
                    )}

                  {canManageGroups && (
                    <Link
                      to="/settings/groups"
                      className={`flex items-center px-4 py-3 text-sm font-medium rounded-md ${
                        isActive("/settings/groups") ? "text-blue-600 bg-blue-50" : "text-gray-700 hover:bg-gray-100"
                      }`}
                      onClick={() => handleNavClick("groups")}
                    >
                      <FaUsers className="mr-3 text-lg" />
                      Team Management
                    </Link>
                  )}

                  {currentUserRole === "ROLE_ADMINISTRATOR" && (
                    <Link
                      to="/settings/amenities"
                      className={`flex items-center px-4 py-3 text-sm font-medium rounded-md ${
                        isActive("/settings/amenities") ? "text-blue-600 bg-blue-50" : "text-gray-700 hover:bg-gray-100"
                      }`}
                      onClick={() => handleNavClick("amenities")}
                    >
                      <FaCog className="mr-3 text-lg" />
                      Amenities Management
                    </Link>
                  )}

                  {currentUserRole === "ROLE_ADMINISTRATOR" && (
                    <Link
                      to="/settings/revenue-share"
                      className={`flex items-center px-4 py-3 text-sm font-medium rounded-md ${
                        isActive("/settings/revenue-share")
                          ? "text-blue-600 bg-blue-50"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                      onClick={() => handleNavClick("revenue_share")}
                    >
                      <FaPercent className="mr-3 text-lg" />
                      Revenue Share Management
                    </Link>
                  )}

                  {currentUserRole && currentUserRole !== "ROLE_ADMINISTRATOR" && (
                    <Link
                      to="/settings/danger-zone"
                      className={`flex items-center px-4 py-3 text-sm font-medium rounded-md ${
                        isActive("/settings/danger-zone") ? "text-red-600 bg-red-50" : "text-red-600 hover:bg-red-50"
                      }`}
                      onClick={() => handleNavClick("danger_zone")}
                    >
                      <FaExclamationTriangle className="mr-3 text-lg" />
                      Danger Zone
                    </Link>
                  )}
                </nav>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 p-4 md:p-6">
              <Switch>
                <Route
                  exact
                  path="/settings"
                  render={() => (
                    <Profile
                      currentUser={currentUser}
                      onUserUpdate={updateUserEverywhere}
                      countries={countries}
                      currencies={currencies}
                    />
                  )}
                />
                <Route exact path="/settings/security" component={Password} />
                <Route
                  exact
                  path="/settings/company/profile"
                  render={() => <CompanyProfile currentUser={currentUser} />}
                />
                <Route exact path="/settings/groups" component={Groups} />
                <Route exact path="/settings/amenities" component={AmenitiesManagement} />
                <Route exact path="/settings/revenue-share" component={RevenueShareManagement} />
                <Route exact path="/settings/danger-zone" component={DangerZone} />
                <Route exact path="/settings/momopayment" component={MomoPayment} />
              </Switch>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
